/* eslint-disable max-lines */
import type { MembershipTierId } from './membership';

export const REWARD_POOL_STATUS = {
  ALL: 'ALL',
  COMPLETED: 'COMPLETED',
  ACTIVE: 'ACTIVE',
  CLAIMED: 'CLAIMED',
  PENDING: 'PENDING',
  DAILY_FEATURED: 'DAILY_FEATURED',
} as const;

export type RewardPoolStatusName = keyof typeof REWARD_POOL_STATUS;

export type RewardPoolStatus = (typeof REWARD_POOL_STATUS)[RewardPoolStatusName];

export const REWARD_POOL_TAG = {
  FEATURED: 'FEATURED',
  HOT: 'HOT',
  HUGE_REWARD: 'HUGE_REWARD',
  TRENDING: 'TRENDING',
  LAST_CHANCE: 'LAST_CHANCE',
} as const;

export type RewardPoolTagName = keyof typeof REWARD_POOL_TAG;

export type RewardPoolTag = (typeof REWARD_POOL_TAG)[RewardPoolTagName];

export const REWARD_POOL_DISTRIBUTION_POLICY = {
  PRO_RATA_KARMA: 'PRO_RATA_KARMA',
  PRO_RATA_SWAP: 'PRO_RATA_SWAP',
  FCFS: 'FCFS',
  FCFS_V2: 'FCFS_V2',
  RAFFLE: 'RAFFLE',
  RAFFLE_V2: 'RAFFLE_V2',
  RAFFLE_SWAP: 'RAFFLE_SWAP',
} as const;

export type RewardPoolDistributionPolicyName = keyof typeof REWARD_POOL_DISTRIBUTION_POLICY;

export type RewardPoolDistributionPolicy =
  (typeof REWARD_POOL_DISTRIBUTION_POLICY)[RewardPoolDistributionPolicyName];

export const REWARD_POOL_SWAP_TYPE = {
  SELL: 'sell',
  BUY: 'buy',
  ALL: 'all',
} as const;

export type RewardPoolSwapType = (typeof REWARD_POOL_SWAP_TYPE)[keyof typeof REWARD_POOL_SWAP_TYPE];

export type RewardPoolDistributionSettings = {
  swap_token?: string;
  swap_chain?: number;
  swap_type: RewardPoolSwapType;
  swap_entry_price?: number;
};

export const REWARD_POOL_CURRENCY = {
  SQR: 'SQR',
  KARMA: 'KARMA',
  USDT: 'USDT',
  CUSTOM_REWARD: 'CUSTOM_REWARD',
} as const;

export type RewardPoolCurrencyName = keyof typeof REWARD_POOL_CURRENCY;

export type RewardPoolCurrency = (typeof REWARD_POOL_CURRENCY)[RewardPoolCurrencyName];

export const RewardPoolCurrencyConfig: Record<RewardPoolCurrency, { title: string }> = {
  [REWARD_POOL_CURRENCY.KARMA]: {
    title: 'Karma',
  },
  [REWARD_POOL_CURRENCY.SQR]: {
    title: 'SQR',
  },
  [REWARD_POOL_CURRENCY.USDT]: {
    title: 'USDT',
  },
  [REWARD_POOL_CURRENCY.CUSTOM_REWARD]: {
    title: 'Custom Reward',
  },
};

export const REWARD_POOL_ACCESS_POLICY_OPERATOR = {
  OR: 'OR',
  AND: 'AND',
} as const;

export type RewardPoolAccessPolicyOperatorName = keyof typeof REWARD_POOL_ACCESS_POLICY_OPERATOR;

export type RewardPoolAccessPolicyOperator =
  (typeof REWARD_POOL_ACCESS_POLICY_OPERATOR)[RewardPoolAccessPolicyOperatorName];

export const REWARD_POOL_ACCESS_POLICY_TYPE = {
  MEMBERSHIP_TIERS: 'MEMBERSHIP_TIERS',
  KARMA_LIMIT: 'KARMA_LIMIT',
  HOT_OFFERS_CLAIMED: 'HOT_OFFERS_CLAIMED',
  WHITELIST: 'WHITELIST',
  HOT_OFFERS_AMOUNT: 'HOT_OFFERS_AMOUNT',
  APPLICATIONS_VOTED: 'APPLICATIONS_VOTED',
  STAKED_SQR: 'STAKED_SQR',
  HOLDING_SQR: 'HOLDING_SQR',
  HOLDING_SQRP: 'HOLDING_SQRP',
  HOLDING_SQRV: 'HOLDING_SQRV',
  POW_OFFERS_AMOUNT: 'POW_OFFERS_AMOUNT',
  POW_OFFERS_CLAIMED: 'POW_OFFERS_CLAIMED',
  REFERRED_AMOUNT: 'REFERRED_AMOUNT',
  PROJECT_PARTICIPANT: 'PROJECT_PARTICIPANT',
  DEPOSIT_VOLUME: 'DEPOSIT_VOLUME',
  DEPOSIT_TURBO_CHARGE_VOLUME: 'DEPOSIT_TURBO_CHARGE_VOLUME',
  APPLICATIONS_VOTED_AMOUNT: 'APPLICATIONS_VOTED_AMOUNT',
  USER_DAILY_LOGIN_AMOUNT: 'USER_DAILY_LOGIN_AMOUNT',
  USER_ACCOUNT_AGE: 'USER_ACCOUNT_AGE',
  USER_DISCORD_ROLE: 'USER_DISCORD_ROLE',
  SWAP_TOTAL_IN_USD: 'SWAP_TOTAL_IN_USD',
  SWAP_TOKEN_TOTAL: 'SWAP_TOKEN_TOTAL',
  TICKET: 'TICKET',
  LIVENESS_COMPLETED: 'LIVENESS_COMPLETED',
  KYC_COMPLETED: 'KYC_COMPLETED',
  BANXA_BUY_SQR: 'BANXA_BUY_SQR',
  STAKED_IN_FARM_POOLS: 'STAKED_IN_FARM_POOLS',
} as const;

export type RewardPoolAccessPolicyTypeName = keyof typeof REWARD_POOL_ACCESS_POLICY_TYPE;

export type RewardPoolAccessPolicyType =
  (typeof REWARD_POOL_ACCESS_POLICY_TYPE)[RewardPoolAccessPolicyTypeName];

export interface RewardPoolAccessPolicyBase {
  // name: RewardPoolAccessPolicyType;
  options: Record<string, unknown>;
  description?: string;
  start_date?: string;
  end_date?: string;
}

export const REWARD_POOL_TICKET_CURRENCY = {
  SQR: 'SQR',
  SQRp: 'SQRp',
  USDT: 'USDT',
  FREE: 'FREE',
};

export type RewardPoolTicketCurrencyName = keyof typeof REWARD_POOL_TICKET_CURRENCY;

export type RewardPoolTicketCurrency =
  (typeof REWARD_POOL_TICKET_CURRENCY)[RewardPoolTicketCurrencyName];

export interface RewardPoolTicketCurrencyType {
  currency: RewardPoolTicketCurrency;
  price: number;
}

export type RewardPoolAccessPolicySuperset = {
  [REWARD_POOL_ACCESS_POLICY_TYPE.MEMBERSHIP_TIERS]: {
    options: {
      membership_tiers: MembershipTierId[];
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.KARMA_LIMIT]: {
    options: {
      // NOTE: not sure why it's optional - ask backend
      minimal_karma?: number; // min 1; max 2147483646
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED]: {
    options: {
      hot_offers_claimed: string[]; // hot offers ids
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.WHITELIST]: {
    options: Record<string, never>;
  };

  [REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_AMOUNT]: {
    options: {
      claimed_amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED]: {
    options: {
      app_ids: number[];
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_SQR]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED_AMOUNT]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.USER_DAILY_LOGIN_AMOUNT]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.USER_ACCOUNT_AGE]: {
    options: {
      daysAmount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE]: {
    options: {
      rolesIds: string[];
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQR]: {
    options: {
      minAmount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRP]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRV]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_AMOUNT]: {
    options: {
      claimed_amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED]: {
    options: {
      hot_offers_claimed: number[];
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.REFERRED_AMOUNT]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT]: {
    options: {
      projectId: string;
      amount?: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_VOLUME]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_TURBO_CHARGE_VOLUME]: {
    options: {
      amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOKEN_TOTAL]: {
    options: {
      minAmount: number;
      // TODO: data from web3?
      tokenSymbol: string;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOTAL_IN_USD]: {
    options: {
      minAmount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.TICKET]: {
    options: {
      limitPerUser: number;
      currencyTypes: Array<RewardPoolTicketCurrencyType>;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.BANXA_BUY_SQR]: {
    options: {
      usdt_amount: number;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_IN_FARM_POOLS]: {
    options: {
      validityPeriodDays: number;
      minAmount: number;
      farmPoolIds?: string[];
    };
  };
};

export type RewardPoolAccessPolicyTypeOf<P extends RewardPoolAccessPolicyType> =
  RewardPoolAccessPolicyBase & {
    name: P;
  } & (P extends keyof RewardPoolAccessPolicySuperset ? RewardPoolAccessPolicySuperset[P] : object);

export type RewardPoolMembershipTiersAccessPolicy = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.MEMBERSHIP_TIERS
>;

export type RewardPoolKarmaLimitAccessPolicy = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.KARMA_LIMIT
>;

export type RewardPoolHotOffersClaimedAccessPolicy = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED
>;

export type RewardPoolWhitelistAccessPolicy = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.WHITELIST
>;

export type RewardPoolHotOffersAmountAccessPolicy = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_AMOUNT
>;

export type RewardPoolAppsVotedAccessPolicy = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED
>;

export type RewardPoolAccessPolicyStakedSqr = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_SQR
>;

export type RewardPoolAccessPolicyApplicationsVotedAmount = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED_AMOUNT
>;

export type RewardPoolAccessPolicyUserDailyLogins = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.USER_DAILY_LOGIN_AMOUNT
>;

export type RewardPoolAccessPolicyUserAccountAge = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.USER_ACCOUNT_AGE
>;

export type RewardPoolAccessPolicyUserDiscordRole = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE
>;

export type RewardPoolAccessPolicyHoldingSqr = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQR
>;

export type RewardPoolAccessPolicyHoldingSqrp = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRP
>;

export type RewardPoolAccessPolicyHoldingSqrv = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRV
>;

export type RewardPoolAccessPolicyPowOffersAmount = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_AMOUNT
>;

export type RewardPoolAccessPolicyPowOffersClaimed = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED
>;

export type RewardPoolAccessPolicyReferredAmount = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.REFERRED_AMOUNT
>;

export type RewardPoolAccessPolicyProjectParticipation = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT
>;

export type RewardPoolAccessPolicyDepositVolume = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_VOLUME
>;

export type RewardPoolAccessPolicyDepositTurboChargeVolume = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_TURBO_CHARGE_VOLUME
>;

export type RewardPoolAccessPolicySwapTokenTotal = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOKEN_TOTAL
>;

export type RewardPoolAccessPolicySwapTokenTotalInUSDT = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOTAL_IN_USD
>;

export type RewardPoolAccessPolicyTicket = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.TICKET
>;

export type RewardPoolAccessPolicyUserProfileLivenessVerification = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.LIVENESS_COMPLETED
>;

export type RewardPoolAccessPolicyUserProfileKycVerification = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.KYC_COMPLETED
>;

export type RewardPoolAccessPolicyBanxaBySqr = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.BANXA_BUY_SQR
>;

export type RewardPoolAccessPolicyStakedInFarmPools = RewardPoolAccessPolicyTypeOf<
  typeof REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_IN_FARM_POOLS
>;

export type RewardPoolAccessPolicyItem =
  | RewardPoolMembershipTiersAccessPolicy
  | RewardPoolKarmaLimitAccessPolicy
  | RewardPoolHotOffersClaimedAccessPolicy
  | RewardPoolWhitelistAccessPolicy
  | RewardPoolHotOffersAmountAccessPolicy
  | RewardPoolAppsVotedAccessPolicy
  | RewardPoolAccessPolicyStakedSqr
  | RewardPoolAccessPolicyHoldingSqr
  | RewardPoolAccessPolicyHoldingSqrp
  | RewardPoolAccessPolicyHoldingSqrv
  | RewardPoolAccessPolicyPowOffersAmount
  | RewardPoolAccessPolicyPowOffersClaimed
  | RewardPoolAccessPolicyReferredAmount
  | RewardPoolAccessPolicyProjectParticipation
  | RewardPoolAccessPolicyDepositVolume
  | RewardPoolAccessPolicyDepositTurboChargeVolume
  | RewardPoolAccessPolicyApplicationsVotedAmount
  | RewardPoolAccessPolicyUserDailyLogins
  | RewardPoolAccessPolicyUserAccountAge
  | RewardPoolAccessPolicyUserDiscordRole
  | RewardPoolAccessPolicySwapTokenTotal
  | RewardPoolAccessPolicySwapTokenTotalInUSDT
  | RewardPoolAccessPolicyTicket
  | RewardPoolAccessPolicyUserProfileLivenessVerification
  | RewardPoolAccessPolicyUserProfileKycVerification
  | RewardPoolAccessPolicyBanxaBySqr
  | RewardPoolAccessPolicyStakedInFarmPools;

export type RewardPoolAccessPolicy = {
  operator: RewardPoolAccessPolicyOperator;
  policies: RewardPoolAccessPolicyItem[];
};

export interface RewardPoolsStats {
  totalSqr: number;
  totalKarma: number;
  poolsStarted: number;
  totalParticipants: number;
  poolsAvailable: number;
  sqrAvailable: number;
  karmaAvailable: number;
}

export interface SelfRewardPoolsStats {
  mySqr: number;
  myKarma: number;
  poolsJoined: 0;
}

export interface RewardPool {
  id: number;
  name: string;
  description: string;
  logo: string;
  banner: string;
  start_date: string;
  end_date: string;
  stopped_at: string | null;
  rewards_sent_at: string | null;
  recreation: boolean;
  recreations_counter: number | null;
  recreations_limit: number;
  legit: boolean;
  security_limit_sqr: number;
  security_limit_time: number;
  status: RewardPoolStatus;
  access_policy: RewardPoolAccessPolicy;
  reward_currency: RewardPoolCurrency;
  custom_reward_options: {
    title?: string;
  };
  reward_amount: number;
  distribution_policy: RewardPoolDistributionPolicy;
  distribution_settings?: RewardPoolDistributionSettings;
  /**
   * If it's RAFFLE it can be null
   */
  participants_max: number | null;
  participants_counter: number;
  entries_max: number | null;
  entries_counter: number | null;
  number_of_winners: number | null;
  points_total: number;
  sqr_calculation_price: number | null;
  tags?: RewardPoolTag[];
  minor_settings: {
    coming_soon: boolean;
    last_chance: number;
  };
  created_at: string;
  updated_at: string;
  userJoined?: boolean;
  completion_trigger: RewardPoolCompletionTrigger;
}

export interface SelfRewardPoolAccessPolicyBase {
  qualified: boolean;
}

interface ExpandedDiscordRole {
  id: string;
  name: string;
  userHasRole: boolean;
}

export type SelfRewardPoolAccessPolicySuperset = {
  [REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED]: {
    hotOffersExpanded?: {
      id: number;
      slug: string;
      name: string;
      isClaimed: boolean;
    }[];
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED]: {
    appsExpanded?: {
      id: number;
      slug: string;
      name: string;
    }[];
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED]: {
    hotOffersExpanded?: {
      id: number;
      slug: string;
      name: string;
    }[];
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT]: {
    project?: {
      id: string;
      slug: string;
      name: string;
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.TICKET]: {
    options: {
      currencyTypes: {
        price: number;
        currency: RewardPoolTicketCurrency;
      }[];
    };
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE]: {
    rolesExpanded: Array<ExpandedDiscordRole>;
  };
  [REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_IN_FARM_POOLS]: {
    farmPoolsExpanded: { id: string; label: string; symbol: string }[];
  };
};

export type SelfRewardPoolAccessPolicyTypeOf<P extends RewardPoolAccessPolicyType> =
  SelfRewardPoolAccessPolicyBase &
    RewardPoolAccessPolicyTypeOf<P> &
    (P extends keyof SelfRewardPoolAccessPolicySuperset
      ? SelfRewardPoolAccessPolicySuperset[P]
      : object);

export type SelfRewardPoolAccessPolicy =
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_CLAIMED>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.KARMA_LIMIT>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.WHITELIST>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.MEMBERSHIP_TIERS>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOT_OFFERS_AMOUNT>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_SQR>
  | SelfRewardPoolAccessPolicyTypeOf<
      typeof REWARD_POOL_ACCESS_POLICY_TYPE.APPLICATIONS_VOTED_AMOUNT
    >
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.USER_DAILY_LOGIN_AMOUNT>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.USER_ACCOUNT_AGE>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOKEN_TOTAL>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.SWAP_TOTAL_IN_USD>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.USER_DISCORD_ROLE>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQR>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRP>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.HOLDING_SQRV>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_AMOUNT>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.POW_OFFERS_CLAIMED>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.REFERRED_AMOUNT>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.PROJECT_PARTICIPANT>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_VOLUME>
  | SelfRewardPoolAccessPolicyTypeOf<
      typeof REWARD_POOL_ACCESS_POLICY_TYPE.DEPOSIT_TURBO_CHARGE_VOLUME
    >
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.TICKET>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.LIVENESS_COMPLETED>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.KYC_COMPLETED>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.BANXA_BUY_SQR>
  | SelfRewardPoolAccessPolicyTypeOf<typeof REWARD_POOL_ACCESS_POLICY_TYPE.STAKED_IN_FARM_POOLS>;

export interface RewardPoolLeaderboardEntry {
  created_at: string;
  currency: RewardPoolTicketCurrency;
  id: string;
  reward: number | null;
}

export interface RewardPoolLeaderboardUser {
  avatar: string;
  displayedName: string | null;
  reward: number | null;
  points: number | null;
  membership: MembershipTierId;
  name: string;
  rank: number;
  user_id: number;
  karma?: number;
  entries?: RewardPoolLeaderboardEntry[];
}

export interface RewardPoolSelfEntry {
  id: string;
  user_id: number;
  pool_id: number;
  currency: RewardPoolTicketCurrency;
  created_at: string;
  updated_at: string;
  reward: null | number; // A number value indicates a win
}

export const REWARD_POOL_COMPLETION_TRIGGER = {
  TIME: 'TIME',
  SOLD_OUT: 'SOLD_OUT',
  TIME_OR_SOLD_OUT: 'TIME_OR_SOLD_OUT',
} as const;

export type RewardPoolCompletionTriggerName = keyof typeof REWARD_POOL_COMPLETION_TRIGGER;

export type RewardPoolCompletionTrigger =
  (typeof REWARD_POOL_COMPLETION_TRIGGER)[RewardPoolCompletionTriggerName];
